/**
 * DeleteGroupModal Component
 *
 * Modal for confirming group deletion with code verification.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteGroup } from '../hooks/useDeleteGroup';
import { useAuth } from '../../../contexts/AuthContext';
import type { Group } from '../../../types/group.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface DeleteGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: Group;
  onSuccess?: () => void;
}

/**
 * DeleteGroupModal component
 */
export const DeleteGroupModal: React.FC<DeleteGroupModalProps> = ({
  isOpen,
  onClose,
  group,
  onSuccess,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';
  const [confirmationCode, setConfirmationCode] = useState<string>('');

  // Delete mutation
  const deleteMutation = useDeleteGroup(tenantId);

  const handleClose = () => {
    if (!deleteMutation.isPending) {
      setConfirmationCode('');
      deleteMutation.reset();
      onClose();
    }
  };

  const handleDelete = async () => {
    if (confirmationCode === group.groupCode) {
      try {
        await deleteMutation.mutateAsync(group.groupId);
        setConfirmationCode('');
        onSuccess?.();
        onClose();
      } catch (error) {
        // Error is handled by the mutation
        console.error('Failed to delete group:', error);
      }
    }
  };

  const isCodeValid = confirmationCode === group.groupCode;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>
        <span className="text-red-600">Delete Group</span>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Error Alert */}
          {deleteMutation.error && (
            <Alert variant="error">
              {getErrorMessage(deleteMutation.error)}
            </Alert>
          )}

          {/* Warning */}
          <Alert variant="warning">
            <div className="space-y-2">
              <p className="font-semibold">This action cannot be undone!</p>
              <p className="text-sm">
                Deleting this group will permanently remove:
              </p>
              <ul className="text-sm list-disc list-inside ml-2">
                <li>The group configuration and metadata</li>
                <li>All member associations</li>
                <li>Role assignments linked to this group</li>
              </ul>
            </div>
          </Alert>

          {/* Group Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-700">Group Name:</span>
              <p className="text-gray-900 font-semibold">{group.groupName}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Group Code:</span>
              <p className="text-gray-900 font-mono bg-gray-200 px-2 py-1 rounded inline-block mt-1">
                {group.groupCode}
              </p>
            </div>
            {group.users && group.users.length > 0 && (
              <div>
                <span className="text-sm font-medium text-gray-700">Users:</span>
                <p className="text-gray-900">{group.users.length} user(s)</p>
              </div>
            )}
          </div>

          {/* Confirmation Input */}
          <div>
            <Input
              label={
                <span>
                  Type <code className="bg-gray-200 px-1 rounded font-mono text-sm">{group.groupCode}</code> to confirm
                </span>
              }
              placeholder="Enter group code"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              disabled={deleteMutation.isPending}
              autoComplete="off"
            />
            {confirmationCode && !isCodeValid && (
              <p className="mt-1 text-sm text-red-600">
                Group code does not match
              </p>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={!isCodeValid || deleteMutation.isPending}
            loading={deleteMutation.isPending}
          >
            Delete Group
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
